<template>
  <BootstrapAlert
    v-if="submitted"
    variant="success"
    class="mb-3"
    data-testid="feedback.form.success-message"
  >
    <Icon icon="material-symbols:check-circle-outline" class="flex-shrink-0 me-3" :inline="true" />
    <p class="m-0">
      {{
        translate(
          'Thanks for your feedback! We will look into it and get in touch if we need anything more.',
        )
      }}
    </p>
  </BootstrapAlert>

  <FeedbackFormPresentational
    v-else
    :id="formId"
    v-model:type="type"
    v-model:title="title"
    v-model:problem-type="problemType"
    v-model:page-url="pageUrl"
    v-model:summary="summary"
    :problem-type-options="problemTypeOptions"
    :error-messages="errorMessages"
    :loading="loading"
    :server-error="serverError"
    @submit="submitFeedback"
  />
</template>
<script setup>
import axios from 'axios';
import {useLocalisation} from '@/composables/useLocalisation';
import FeedbackFormPresentational from './FeedbackFormPresentational.vue';

/**
 * The feedback form & what it takes to send it. Whoever shows it supplies the button that submits
 * it (`<button type="submit" :form="formId">`) & can read `loading` & `submitted`, or `reset` it.
 */
const props = defineProps({
  // Unique when more than one is on the page, as the submit button refers to the form by it
  formId: {
    type: String,
    default: 'feedback-form',
  },
  // Why Sphere failed to load, when it did: sent along with the feedback, as that is what it is about
  context: {
    type: String,
    default: undefined,
  },
});

const msal = inject('msal', null); // Not there when Sphere failed to start up
const routeLocation = useRoute();
const userStore = useUserStore();
const customersStore = useCustomersStore();
const {translate} = useLocalisation();

const type = ref('feedback');
const title = ref('');
const problemType = ref('');
const pageUrl = ref('');
const summary = ref('');
const loading = ref(false);
const submitted = ref(false);

const errorMessages = ref(new Map());
const serverError = ref(false);

const problemTypeOptions = [
  'Report data is incorrect/does not load',
  'Content is incorrect',
  'Do not have access to specific page',
  'Other',
];

const route = computed(() => routeLocation.path);

watch(type, (newType) => {
  if (newType === 'problem') {
    if (!pageUrl.value) {
      pageUrl.value = window.location.href;
    }
  } else if (newType === 'feedback') {
    problemType.value = '';
    pageUrl.value = '';
  }
});

const reset = () => {
  type.value = 'feedback';
  title.value = '';
  problemType.value = '';
  pageUrl.value = '';
  summary.value = '';
  submitted.value = false;
  loading.value = false;
  serverError.value = false;
  errorMessages.value = new Map();
};

const submitFeedback = async (form) => {
  // Also used when Sphere failed to load (see FeedbackSlideIn): the user or the customer may not be known
  const user = userStore.user;
  const selected = customersStore.selectedCustomer;
  // The receiving function rejects an empty profile or customer, and its queue handler needs the
  // profile's email (it picks who to mail by its domain) & a region: say why there is none.
  const notLoaded = {unavailable: true, reason: 'Sphere failed to load'};
  const signedIn = msal?.instance?.getAllAccounts?.()[0]?.username;
  const email = user?.email ?? (signedIn?.includes('@') ? signedIn : 'unknown@unknown');
  /**
   * CORS error, Azure blocks responses to localhost.
   * However, the request does go through to the function.
   */
  try {
    loading.value = true;
    serverError.value = false;
    errorMessages.value = new Map();

    if (form.value) {
      form.value.classList.remove('was-validated');
    }

    const payload = {
      id: crypto.randomUUID(),
      title: title.value,
      submitted_by: email,
      user_name: [user?.first_name, user?.last_name].filter(Boolean).join(' '),
      user_is_staff: userStore.isEkcoUser,
      route: route.value,
      type: type.value,
      ...(problemType.value && {problem_type: problemType.value}),
      ...(pageUrl.value && {page_url: pageUrl.value}),
      feedback: props.context
        ? `${summary.value}\n\n[Sphere failed to load for this user: ${props.context}]`
        : summary.value,
      customer_name: selected?.customer?.name,
      customer_debtor_number: selected?.customer?.debtor_number,
      fresh_requester_id: user?.fresh_requester_id,
      region: selected?.customer?.region ?? '',
      eventTime: new Date().toUTCString(),
      profile: user ?? {...notLoaded, email},
      customer: selected ?? notLoaded,
    };

    await axios.post(
      `https://ekco-receive-feedback.azurewebsites.net/api/user_feedback?code=${user?.USER_FEEDBACK_API_KEY}`,
      JSON.stringify(payload),
      {
        headers: {
          'Content-Type': 'text/plain',
        },
      },
    );
    submitted.value = true;
  } catch (e) {
    serverError.value = true;
    errorMessages.value.set(
      'submit',
      e?.message ||
        translate(
          'An unexpected error occurred while submitting your feedback. Please try again later.',
        ),
    );
    if (form.value) {
      form.value.classList.remove('was-validated');
    }
  } finally {
    loading.value = false;
  }
};

defineExpose({loading, submitted, reset});
</script>
